import TextInput from 'components/Common/Form/Input/TextInput'
import TextButton from 'components/Luxkit/Button/TextButton'
import CheckboxInput from 'components/Luxkit/Checkbox/CheckboxInput'
import Group from 'components/utils/Group'
import TextLink from 'components/Luxkit/TextLink'
import Heading from 'components/Luxkit/Typography/Heading'
import React, { FormEventHandler, useCallback, useContext, useEffect, useRef, useState } from 'react'
import BusinessTravellerStepper from 'businessTraveller/components/stepper/BusinessTravellerStepGroup'
import { useMutation } from '@tanstack/react-query'
import { checkUserExists } from 'api/auth'
import ReCAPTCHA from 'react-google-recaptcha'
import handleReCAPTCHA from 'businessTraveller/utils/handleReCAPTCHA'
import styled from 'styled-components'
import config from 'constants/config'
import requestCheckEmailAssociation from 'businessTraveller/api/requestCheckEmailAssociation'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { authLogout } from 'actions/AuthActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import {
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_ACTIONS,
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS,
  BusinessTravellerBusinessOnboardingDispatchContext,
} from 'businessTraveller/contexts/BusinessTravellerBusinessOnboardingContexts'
import { formToObject } from 'lib/forms/formToObject'
import { BUSINESS_TRAVELLER_ACTIONS } from 'reducers/businessTravellerActionReducer'
import LayoutContainer from 'components/Common/LayoutContainer'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

const SignupContainer = styled(LayoutContainer)`
  background-color:  ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(24)};
  }
`

const EmailValidationForm = styled.form`
  position: relative;
`

interface EmailValidation {
  businessName: string
  adminEmail: string
}

const stepStatuses: BusinessTraveller.BusinessSignupStepKeyStatuses = {
  validate_email: 'active',
  create_customer: 'pending',
  create_business: 'pending',
}

interface Props {
  steps: BusinessTraveller.BusinessSignupSteps;
}

function BusinessTravellerSignupEmailValidation(props: Props) {
  const { steps } = props
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false)

  const dispatch = useAppDispatch()
  const accountSetupDispatch = useContext(BusinessTravellerBusinessOnboardingDispatchContext)

  const formRef = useRef<HTMLFormElement>(null)
  const businessEmailRef = useRef<HTMLInputElement>(null)
  const reCaptchaRef = useRef<ReCAPTCHA>(null)

  const emailCheckMutation = useMutation({
    mutationFn: async(email: string) => {
      const reCaptchaResponse = await handleReCAPTCHA(reCaptchaRef.current)
      return await checkUserExists({ email }, reCaptchaResponse)
    },
    onSuccess: async(data, email) => {
      if (data?.isExist) {
        // if LE customer exists, check to see if the customer has already been associated with a business.
        lebtBusinessCheckMutation.mutate(email)
      } else {
        // if no LE customer exists, redirect to the CREATE_CUSTOMER step in the onboarding flow.
        accountSetupDispatch({
          type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_ACTIONS.STEP_INTO,
          step: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS.CREATE_CUSTOMER,
        })
        setIsFormSubmitting(false)
      }
    },
    onError: (error: App.ApiCallFailure) => {
      businessEmailRef.current?.setCustomValidity(error.message)
      formRef.current?.checkValidity()
      setIsFormSubmitting(false)
    },
  })

  const lebtBusinessCheckMutation = useMutation({
    mutationFn: async(email: string) => {
      await requestCheckEmailAssociation(email)
    },
    onSuccess: async() => {
      accountSetupDispatch({
        type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_ACTIONS.STEP_INTO,
        step: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS.BUSINESS_EXISTS,
      })
      setIsFormSubmitting(false)
    },
    onError: async(error: App.ApiCallFailure) => {
      // If the email is not registered for a business. The following error is returned.
      // In this case we want to redirect the customer to the LINK_ACCOUNT step of the onboarding flow.
      if (error.message === 'This email is not registered.') {
        accountSetupDispatch({
          type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_ACTIONS.STEP_INTO,
          step: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS.LINK_ACCOUNT,
        })
      } else {
        // report all other errors
        businessEmailRef.current?.setCustomValidity(error.message)
        formRef.current?.checkValidity()
      }
      setIsFormSubmitting(false)
    },
  })

  const emailOnChange = useCallback(() => {
    businessEmailRef.current?.setCustomValidity('')
    businessEmailRef.current?.reportValidity()
  }, [])

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    setIsFormSubmitting(true)
    const formData = formToObject<EmailValidation>(event.currentTarget)
    accountSetupDispatch({
      type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_ACTIONS.SET_BUSINESS_AND_EMAIL,
      businessName: formData.businessName,
      adminEmail: formData.adminEmail,
    })
    emailCheckMutation.mutate(formData.adminEmail)
  }, [accountSetupDispatch, emailCheckMutation])

  // Force logout and remove all business data from redux.
  // Not doing this causes the LINK_ACCOUNT component to flash before CREATE_BUSINESS.
  useEffect(() => {
    dispatch(authLogout())
    dispatch({ type: BUSINESS_TRAVELLER_ACTIONS.RESET })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <SignupContainer>
      <EmailValidationForm onSubmit={handleSubmission} ref={formRef}>
        {isFormSubmitting && <LoadingIndicator floating />}
        <Group direction="vertical" gap={24}>
          <Heading variant="heading3">Join now for free</Heading>
          <BusinessTravellerStepper steps={steps} stepStatuses={stepStatuses}/>
          <Group direction="vertical" gap={8}>
            <TextInput
              label="Business name"
              maxLength={100}
              minLength={2}
              name="businessName"
              placeholder="Enter your business name"
              pattern="^[A-Za-z0-9 !#$%&'\(\)*,\-.\/:;=?@\\_\{\}\|]+$"
              invalidErrorMessage="Invalid characters in business name."
              required
              requiredErrorMessage="Please enter a business name"
            />
            <Group direction="vertical">
              <TextInput
                type="email"
                label="Business email"
                name="adminEmail"
                placeholder="Enter your business email"
                required
                requiredErrorMessage="Please enter your email address"
                ref={businessEmailRef}
                onChange={emailOnChange}
              />
            </Group>
            <CheckboxInput name="checkbox::boolean" required>
              I confirm that I am an authorised representative of the agency. I have read and accept the <TextLink target="_blank" to="/terms-and-conditions" underline variant="black">Terms and Conditions</TextLink> and <TextLink target="_blank" to="/privacy-policy" underline variant="black">Privacy Policy</TextLink>
            </CheckboxInput>
          </Group>
          <Group direction="horizontal" horizontalAlign="end">
            <TextButton
              fit="mobile-full-width"
              kind="primary"
              shape="square"
              size="medium"
              type="submit"
              disabled={isFormSubmitting}
            >
              Continue
            </TextButton>
          </Group>
        </Group>
        {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reCaptchaRef} />}
      </EmailValidationForm>
    </SignupContainer>
  )
}

export default BusinessTravellerSignupEmailValidation
